import { useToast } from '@/components/ui/use-toast';
import { toSalesAddress } from '@/features/sales-draft-header/components/form-shipping-address-field';
import { useAppState } from '@/lib/hooks/use-app-state';
import type { SalesDraftDefaultHeaderOutput, SalesDraftType } from '@/server/types/drafts-types';
import { api } from '@/utils/api';
import { SrCustomerAddressType } from '@plato/data/starrocks/types';
import { useEffect, useMemo } from 'react';

type Options = {
    customerId: string | null | undefined;
    orderType: SalesDraftType;
};

export const useSalesDraftDefaults = ({ customerId, orderType }: Options) => {
    const { platoUser } = useAppState();
    const { toast } = useToast();

    const {
        isLoading: isLoadingQuery,
        data: headerDefaults,
        error,
        isError,
    } = api.platoUser.query.salesDraft.getSalesDraft.getDefaultHeader.useQuery(
        {
            customer_id: customerId || '',
            clerk_id: platoUser?.erpUserId || '',
            sales_order_type: orderType,
        },
        {
            enabled: !!customerId,
        },
    );

    const { data: addresses, isLoading: isLoadingPrimaryAddresses } =
        api.platoUser.getManyCustomerAddress.get.useQuery(
            {
                customerId: customerId || '',
            },
            {
                enabled: !!customerId,
            },
        );

    const isLoadingDefault = customerId ? isLoadingQuery || isLoadingPrimaryAddresses : false;

    const srPrimaryAddress = useMemo(() => {
        return addresses?.find((a) => a.type === SrCustomerAddressType.PRIMARY);
    }, [addresses]);

    const srBillingAddress = useMemo(() => {
        return addresses?.find((a) => a.type === SrCustomerAddressType.BILLING) ?? srPrimaryAddress;
    }, [addresses, srPrimaryAddress]);

    // Enhanced defaults with address fallback logic
    const defaults = useMemo<SalesDraftDefaultHeaderOutput | undefined>(() => {
        if (!headerDefaults) {
            return undefined;
        }

        if (headerDefaults.invoice_address && headerDefaults.shipping_address) {
            return headerDefaults;
        }

        if (isLoadingPrimaryAddresses) {
            return undefined;
        }

        if (!srPrimaryAddress || !srBillingAddress) {
            return headerDefaults;
        }

        return {
            ...headerDefaults,
            shipping_address: headerDefaults.invoice_address ?? toSalesAddress(srBillingAddress),
            invoice_address: headerDefaults.shipping_address ?? toSalesAddress(srPrimaryAddress),
        };
    }, [headerDefaults, srPrimaryAddress, srBillingAddress, isLoadingPrimaryAddresses]);

    useEffect(() => {
        if (isError) {
            toast({
                title: 'error',
                description: error.message,
                icon: 'warning',
            });
        }
    }, [isError, error]);

    return {
        isLoadingDefault,
        defaults,
    };
};
